/**
 * Reservations: the list, taking one, and one in full.
 *
 * **The stage between an enquiry and a sale, which the product could not see.** Somebody expresses
 * interest, pays a holding fee, and what they are holding either becomes a purchase or goes back on
 * the market. ADR-0109 is the reasoning; three of its rules decide almost everything on this screen
 * and each of them is easy to break by accident.
 *
 * 1. **A property holds defaults; a reservation holds terms.** All five settings — whether it takes
 *    the thing off the market, whether it expires and after how long, whether the fee comes back,
 *    and the fee itself — are copied at the moment it is taken and frozen. So the form below sends
 *    a key **only when somebody changed it**: an absent key means "the property decides", and
 *    `null` is an answer in its own right. `{ days: null }` says *this* reservation never expires
 *    even though the property's default is fourteen days; `{}` says take the fourteen. Collapsing
 *    the two would make the first impossible to say, and it is an ordinary thing to want to say
 *    about one buyer. `changedTerms()` below is where that distinction is kept.
 *
 * 2. **`expired` is derived, never stored.** A reservation is held, converted or lapsed; running
 *    out is worked out from the date, exactly as an instalment's "overdue" is. So this screen shows
 *    `state` and `stateLabel` and never the raw `status` — a screen reading the status would say
 *    "held" about a reservation that ran out on Tuesday, because the sweep that lapses it is a
 *    nightly job and saying it has run out needs nothing to have run.
 *
 * 3. **A fee nobody named is not a fee of nothing.** `fee.set === false` means the property quotes
 *    no reservation fee and this one did not name one either. Rendering that as ₦0.00 tells a buyer
 *    there is nothing to pay, which is a decision somebody is entitled to make and therefore not
 *    one to arrive at by accident. It reads "no fee set".
 *
 * And the fee **counts toward the price** rather than sitting on top of it. That is the sentence
 * this screen and the buyer's statement both have to carry, because a schedule that asks for money
 * the buyer has already paid is a schedule they will ring about.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PURCHASE_FREQUENCIES } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { minor, titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { usePropertyChoices, useSoldNoun } from "@/lib/development";
import { useListParams } from "@/lib/list-params";
import { resolveParty, searchParties } from "@/lib/pickers";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Switch,
  Table,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { SearchSelect } from "@/components/search-select";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { Refusal } from "@/components/padlock";
import { CommissionOverrides, MarketerPicker } from "./Marketers";

/* ------------------------------------------------------------------- shapes */

/** One row of the list, as the server decorates it. `state` already includes the derived expiry. */
interface ReservationRow {
  id: string;
  reference: string;
  state: "held" | "expired" | "converted" | "lapsed";
  stateLabel: string;
  reservedOn: string;
  expiresOn: string | null;
  feeAmount: number;
  feeAmountLabel: string;
  refundable: boolean;
  holds: boolean;
  paid: number;
  paidLabel: string;
  buyerId: string;
  buyerName: string;
  estateId: string;
  estateName: string;
  plotId: string | null;
  plotLabel: string | null;
  purchaseId: string | null;
  purchaseReference: string | null;
}

interface ReservationDetail {
  reservation: ReservationRow & {
    feeBasis: "fixed" | "percent";
    feeValue: number | null;
    quotedPrice: number | null;
    notes: string | null;
    lapsedReason: string | null;
    convertedAt: string | null;
  };
  buyer: { id: string; name: string; phone: string | null; email: string | null };
  estate: { id: string; name: string };
  noun: { one: string; many: string };
  plot: { id: string; label: string; status: string } | null;
  receipts: Array<{
    id: string;
    receiptNumber: string;
    amount: number;
    method: string;
    receivedOn: string;
    status: string;
    reference: string | null;
    purchaseId: string | null;
  }>;
  fee: {
    quoted: number;
    quotedLabel: string;
    paid: number;
    paidLabel: string;
    outstanding: number;
    outstandingLabel: string;
    /** False when nobody has named a fee. Never rendered as a fee of nothing. */
    set: boolean;
    quotedPrice: number | null;
  };
  purchase: { id: string; reference: string; status: string } | null;
  today: string;
}

const STATE_TONE: Record<string, "success" | "warn" | "brand" | "muted"> = {
  held: "success",
  expired: "warn",
  converted: "brand",
  lapsed: "muted",
};

/* ------------------------------------------------------------------- terms */

/**
 * The five settings as a screen holds them, which is not quite the shape the API takes.
 *
 * Two differences, both deliberate. The fee is **typed** rather than stored — a percentage when the
 * basis is `percent` and a money figure when it is `fixed` — because those are different units in
 * one column and the box has to know which it is showing. And expiry is a switch beside a number
 * rather than one nullable field, because "does it expire" and "after how long" are two questions a
 * person answers separately even though the database has only one honest way to record the pair.
 */
export interface TermsDraft {
  feeBasis: "fixed" | "percent";
  feeValue: string;
  holds: boolean;
  refundable: boolean;
  expires: boolean;
  days: string;
}

/** What the API stores, in either vocabulary: minor units for a flat fee, basis points for a share. */
export interface TermsValues {
  feeBasis: "fixed" | "percent";
  feeValue: number | null;
  holds: boolean;
  refundable: boolean;
  days: number | null;
}

/** The product's own defaults, for a form with no property behind it yet. `RESERVATION_DEFAULTS`. */
export const BLANK_TERMS: TermsDraft = {
  feeBasis: "percent",
  feeValue: "",
  holds: true,
  refundable: false,
  expires: false,
  days: "",
};

/**
 * A stored figure, as a box shows it.
 *
 * Both units divide by a hundred — basis points to per cent, minor units to whole ones — and both
 * have to keep a **deliberate zero visible**. "0%" is somebody saying no commission is charged and
 * an empty box is nobody having said anything, and a helper that folds the first into the second
 * would quietly change one into the other every time the form was opened and saved.
 */
function valueText(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(Number((value / 100).toFixed(2)));
}

export function draftFrom(terms: Partial<TermsValues> | null | undefined): TermsDraft {
  if (!terms) return BLANK_TERMS;
  return {
    feeBasis: (terms.feeBasis ?? "percent") as "fixed" | "percent",
    feeValue: valueText(terms.feeValue),
    holds: terms.holds ?? true,
    refundable: terms.refundable ?? false,
    expires: terms.days !== null && terms.days !== undefined,
    days: terms.days === null || terms.days === undefined ? "" : String(terms.days),
  };
}

export function termsValues(draft: TermsDraft): TermsValues {
  const typed = draft.feeValue.trim();
  return {
    feeBasis: draft.feeBasis,
    /* Empty is "nobody has said" and stays null. A percentage is stored in basis points for the
       same reason money is stored in minor units: 2.5% is 250, never 0.025000000000000001. */
    feeValue:
      typed === ""
        ? null
        : draft.feeBasis === "percent"
          ? Math.round(Number(typed) * 100)
          : (minor(typed) ?? null),
    holds: draft.holds,
    refundable: draft.refundable,
    days: draft.expires ? Number(draft.days) || 0 : null,
  };
}

/**
 * Only the settings somebody actually moved.
 *
 * **This is the whole of rule 1 above, in five lines.** A key that is absent means "take the
 * property's answer" and a key set to `null` is an answer in its own right, so a form that sent all
 * five every time would freeze the property's current defaults onto every reservation — and then
 * changing a default next month would visibly fail to move what the next buyer is offered, which is
 * the exact opposite of what defaults are for.
 */
export function changedTerms(initial: TermsDraft, draft: TermsDraft): Partial<TermsValues> {
  const was = termsValues(initial);
  const now = termsValues(draft);
  const out: Partial<TermsValues> = {};
  if (now.feeBasis !== was.feeBasis) out.feeBasis = now.feeBasis;
  if (now.feeValue !== was.feeValue) out.feeValue = now.feeValue;
  if (now.holds !== was.holds) out.holds = now.holds;
  if (now.refundable !== was.refundable) out.refundable = now.refundable;
  if (now.days !== was.days) out.days = now.days;
  return out;
}

/** The same five, in the names the property's own record uses. */
export function estateTermsPayload(draft: TermsDraft): Record<string, unknown> {
  const values = termsValues(draft);
  return {
    reservationFeeBasis: values.feeBasis,
    reservationFeeValue: values.feeValue,
    reservationHolds: values.holds,
    reservationRefundable: values.refundable,
    reservationDays: values.days,
  };
}

/** And back the other way, off a property record, for a form that is filling itself in. */
export function draftFromEstate(estate: {
  reservationFeeBasis?: string | null;
  reservationFeeValue?: number | null;
  reservationHolds?: boolean | null;
  reservationRefundable?: boolean | null;
  reservationDays?: number | null;
}): TermsDraft {
  return draftFrom({
    feeBasis: (estate.reservationFeeBasis ?? "percent") as "fixed" | "percent",
    feeValue: estate.reservationFeeValue ?? null,
    holds: estate.reservationHolds ?? true,
    refundable: estate.reservationRefundable ?? false,
    days: estate.reservationDays ?? null,
  });
}

/**
 * The five controls, wherever they are needed.
 *
 * One component for the property's defaults and for a single reservation's terms, because they are
 * the same five questions and a second set of boxes would eventually word one of them differently
 * from the other. `noun` is what this property calls the things inside it, so the switch says "takes
 * the plot off the market" on a land scheme and "takes the unit off the market" on a tower.
 */
export function ReservationTermsFields({
  draft,
  onChange,
  prefix,
  noun,
}: {
  draft: TermsDraft;
  onChange: (next: TermsDraft) => void;
  /** Unique per form on the page, so two of these can be drawn at once without colliding ids. */
  prefix: string;
  noun: { one: string; many: string };
}) {
  const set = (patch: Partial<TermsDraft>) => onChange({ ...draft, ...patch });

  return (
    <>
      <Field
        label="Reservation fee"
        htmlFor={`${prefix}-fee-basis`}
        hint="A share of the asking price, or a flat figure. Leave the amount empty if you do not charge one."
        group
      >
        <div className="flex flex-wrap gap-2">
          <Select
            id={`${prefix}-fee-basis`}
            className="w-40"
            value={draft.feeBasis}
            onChange={(event) =>
              /* The box is cleared when the basis changes, because the number means a different
                 thing on each side of it — 5 is five per cent or five whole units, and carrying it
                 across would silently quote somebody a fee nobody typed. */
              set({ feeBasis: event.target.value as "fixed" | "percent", feeValue: "" })
            }
          >
            <option value="percent">A percentage</option>
            <option value="fixed">A flat amount</option>
          </Select>
          {draft.feeBasis === "percent" ? (
            <Input
              id={`${prefix}-fee-value`}
              className="w-32"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={draft.feeValue}
              onChange={(event) => set({ feeValue: event.target.value })}
              placeholder="10"
              aria-label="Reservation fee, as a percentage"
            />
          ) : (
            <div className="w-44">
              <MoneyInput
                id={`${prefix}-fee-value`}
                value={draft.feeValue}
                onChange={(next) => set({ feeValue: next })}
                placeholder="500,000"
                aria-label="Reservation fee"
              />
            </div>
          )}
        </div>
      </Field>

      <Field label={`Takes the ${noun.one} off the market`} group hint="The usual arrangement: nobody else is offered it while it is held.">
        <Switch
          checked={draft.holds}
          label={`Takes the ${noun.one} off the market`}
          onChange={(next) => set({ holds: next })}
        />
      </Field>

      <Field label="The fee is refundable" group hint="If it comes to nothing, the holding fee goes back to the buyer.">
        <Switch checked={draft.refundable} label="The fee is refundable" onChange={(next) => set({ refundable: next })} />
      </Field>

      <Field label="It expires on its own" group hint="Off by default. A reservation that never expires is ended by hand.">
        <div className="flex flex-wrap items-center gap-3">
          <Switch
            checked={draft.expires}
            label="It expires on its own"
            onChange={(next) => set({ expires: next, days: next && !draft.days ? "14" : draft.days })}
          />
          {draft.expires ? (
            <span className="flex items-center gap-2 text-sm text-ink-600">
              after
              <Input
                className="w-20"
                type="number"
                min={1}
                max={3650}
                value={draft.days}
                onChange={(event) => set({ days: event.target.value })}
                aria-label="Days it stands for"
              />
              days
            </span>
          ) : null}
        </div>
      </Field>
    </>
  );
}

/* -------------------------------------------------------------- the defaults */

/**
 * How this property reserves, on the property's own screen.
 *
 * **Defaults, not terms.** Changing one of these moves what the *next* buyer is offered and touches
 * nothing already held — the same promise the price list makes against a sale already agreed, and
 * the sentence under the card says so, because it is the reassurance that makes the feature usable.
 */
export function ReservationDefaultsCard({
  estateId,
  estate,
  noun,
}: {
  estateId: string;
  estate: {
    reservationFeeBasis?: string | null;
    reservationFeeValue?: number | null;
    reservationHolds?: boolean | null;
    reservationRefundable?: boolean | null;
    reservationDays?: number | null;
  };
  noun: { one: string; many: string };
}) {
  const [draft, setDraft] = useState<TermsDraft>(() => draftFromEstate(estate));

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/estates/${estateId}`, input),
    invalidates: [["estate", estateId], ["estates"]],
  });

  return (
    <Card className="mb-6">
      <CardTitle hint="Copied onto each reservation as it is taken, and frozen there. Changing them moves what the next buyer is offered and nothing already held.">
        How this property reserves
      </CardTitle>

      {save.errorMessage ? (
        <div className="mb-4">
          <Refusal error={save.error} message={save.errorMessage} />
        </div>
      ) : null}

      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(estateTermsPayload(draft));
        }}
      >
        <ReservationTermsFields draft={draft} onChange={setDraft} prefix="estate-reservation" noun={noun} />
        <div className="sm:col-span-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save these defaults"}
          </Button>
          {save.isSuccess ? <span className="ml-3 text-sm text-brand-700">Saved.</span> : null}
        </div>
      </form>
    </Card>
  );
}

/* --------------------------------------------------------------- taking one */

/**
 * Reserving something.
 *
 * Opened from a property, from a single one of the things inside it, or from the reservations list
 * with nothing chosen — which is why the property and the node are both props and both optional.
 * The terms start as the property's own and are sent only where somebody moved them; see
 * `changedTerms`.
 */
export function ReserveModal({
  estateId: fixedEstate,
  plotId: fixedPlot,
  onClose,
}: {
  estateId?: string;
  plotId?: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [estateId, setEstateId] = useState(fixedEstate ?? "");
  const [plotId, setPlotId] = useState(fixedPlot ?? "");
  const [partyId, setPartyId] = useState("");
  const [draft, setDraft] = useState<TermsDraft>(BLANK_TERMS);
  const [initial, setInitial] = useState<TermsDraft>(BLANK_TERMS);
  const [showTerms, setShowTerms] = useState(false);

  const { data: properties } = usePropertyChoices();

  const { data: property } = useQuery({
    queryKey: ["estate", estateId],
    queryFn: () =>
      api.get<{
        estate: {
          reservationFeeBasis: string;
          reservationFeeValue: number | null;
          reservationHolds: boolean;
          reservationRefundable: boolean;
          reservationDays: number | null;
        };
        noun: { one: string; many: string };
        plots: Array<{ id: string; label: string; status: string; sellable: boolean }>;
      }>(`/estates/${estateId}`),
    enabled: Boolean(estateId),
  });

  /*
    The property's defaults, once they arrive.

    Held twice — as the draft somebody edits and as the answer they started from — because what is
    sent is the difference between the two. Reset whenever the property changes, since a second
    property's defaults are a second starting point.
  */
  useEffect(() => {
    if (!property?.estate) return;
    const next = draftFromEstate(property.estate);
    setInitial(next);
    setDraft(next);
  }, [property?.estate]);

  const noun = property?.noun ?? { one: "unit", many: "units" };

  const take = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/reservations", input),
    invalidates: [["reservations"], ["sales"], ["estate", estateId], ["developer-summary"]],
    onSuccess: (result) => {
      onClose();
      navigate(`/app/development/sales/reservations/${result.id}`);
    },
  });

  const changed = changedTerms(initial, draft);

  return (
    <Modal open onClose={onClose} title="Reserve">
      {take.errorMessage ? (
        <div className="mb-4">
          <Refusal error={take.error} message={take.errorMessage} />
        </div>
      ) : null}

      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          const values = formValues(event.currentTarget);
          take.mutate({
            estateId,
            plotId: plotId || null,
            partyId,
            reservedOn: values.reservedOn,
            notes: values.notes || null,
            /* Only what somebody moved. Everything else is the property's to decide, today and
               whenever it is next changed. */
            ...changed,
          });
        }}
      >
        {fixedEstate ? null : (
          <Field label="Property" htmlFor="reserve-estate" required>
            <Select
              id="reserve-estate"
              value={estateId}
              onChange={(event) => {
                setEstateId(event.target.value);
                setPlotId("");
              }}
              required
            >
              <option value="">Choose a property…</option>
              {(properties?.estates ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {fixedPlot ? null : (
          <Field
            label={titleCase(noun.one)}
            htmlFor="reserve-plot"
            hint={`Optional. A reservation with no ${noun.one} holds nothing off the market, and one is allocated later.`}
          >
            <Select id="reserve-plot" value={plotId} onChange={(event) => setPlotId(event.target.value)} disabled={!estateId}>
              <option value="">Decide later</option>
              {(property?.plots ?? [])
                /* Only what is actually for sale and actually available. A grouping cannot be
                   reserved for the same reason it cannot be sold — the things inside it are what a
                   buyer gets — and the API refuses it in those words. */
                .filter((row) => row.sellable && row.status === "available")
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.label}
                  </option>
                ))}
            </Select>
          </Field>
        )}

        <Field label="Buyer" htmlFor="reserve-party" required>
          <SearchSelect
            id="reserve-party"
            value={partyId}
            onChange={setPartyId}
            search={searchParties()}
            resolve={resolveParty}
            placeholder="Search clients…"
          />
        </Field>

        <Field label="Reserved on" htmlFor="reservedOn" required>
          <Input
            id="reservedOn"
            name="reservedOn"
            type="date"
            required
            defaultValue={new Date().toISOString().slice(0, 10)}
          />
        </Field>

        <Field label="Notes" htmlFor="notes">
          <Input id="notes" name="notes" placeholder="Agreed at the site office." />
        </Field>

        {/*
          The terms, folded away.

          The ordinary reservation takes the property's own answers and needs none of these, so
          opening on five extra controls would make the common case the harder one. Somebody who
          needs to say "this one, for this buyer, is different" opens it — and what they change is
          the only thing that gets sent.
        */}
        <div className="rounded-[var(--radius-control)] border border-ink-200 p-3">
          <button
            type="button"
            className="text-sm font-medium text-brand-700 hover:underline"
            onClick={() => setShowTerms((open) => !open)}
          >
            {showTerms ? "Use the property's own terms" : "Different terms for this buyer"}
          </button>
          {showTerms ? (
            <div className="mt-3 grid gap-4">
              <ReservationTermsFields draft={draft} onChange={setDraft} prefix="reserve" noun={noun} />
              <p className="text-xs text-ink-500">
                {Object.keys(changed).length === 0
                  ? "Nothing changed — this reservation takes the property's answers."
                  : `${Object.keys(changed).length} of the five differ from the property and are recorded on this reservation.`}
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={take.isPending || !estateId || !partyId}>
            {take.isPending ? "Reserving…" : "Reserve it"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/*
  The list moved out, and did not come back.

  **A reservation is a sale that has not converted**, so it is a *state* inside Sales rather than a
  screen beside it — item 5 of `14-DEVELOPMENT-MODULE-PLAN.md`. It had its own rail item, its own
  list and its own set of views, all of which asked the reader to carry the thread between two
  screens telling one story. `/app/development/reservations` now opens Sales narrowed to the
  reserved stage.

  Everything else in this file stays, because a hold is still a record with terms, a fee and a page
  of its own: the terms editor the property form reuses, the dialog that takes a hold, the
  reservation's own page, and the form that converts one into a sale.
*/

/* ------------------------------------------------------------ one reservation */

export function ReservationDetailPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const fmt = useFormat();
  const [doing, setDoing] = useState<"fee" | "convert" | "lapse" | "extend" | null>(null);
  /** What lapsing said about a refund. Kept because it is a question the screen has to ask. */
  const [refund, setRefund] = useState<{ due: boolean; amount: number; amountLabel: string } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["reservation", id],
    queryFn: () => api.get<ReservationDetail>(`/reservations/${id}`),
  });

  const invalidates = [["reservation", id], ["reservations"], ["sales"], ["payments"], ["developer-summary"]] as string[][];

  const recordFee = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/reservations/${id}/fee`, input),
    invalidates,
    onSuccess: () => setDoing(null),
  });

  const extend = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/reservations/${id}`, input),
    invalidates,
    onSuccess: () => setDoing(null),
  });

  const lapse = useApiMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ released: boolean; refund: { due: boolean; amount: number; amountLabel: string } }>(
        `/reservations/${id}/lapse`,
        input,
      ),
    invalidates: [...invalidates, ["estates"]],
    onSuccess: (result) => {
      setDoing(null);
      setRefund(result.refund);
    },
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { reservation, buyer, estate, plot, fee, noun, purchase } = data;
  const standing = reservation.state === "held";
  const failure = recordFee.error ?? extend.error ?? lapse.error;
  const failureMessage = recordFee.errorMessage ?? extend.errorMessage ?? lapse.errorMessage;

  return (
    <div>
      <PageHeader
        title={buyer.name}
        back={{ to: "/app/development/sales?stage=reserved", label: "Sales" }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>{reservation.reference}</span>
            <Badge tone={STATE_TONE[reservation.state] ?? "muted"}>{reservation.stateLabel}</Badge>
            <span>
              · {estate.name}
              {plot ? ` · ${plot.label}` : ""}
            </span>
          </span>
        }
        description={`Taken on ${reservation.reservedOn}. The holding fee counts toward the price, not on top of it.`}
      />

      {failureMessage ? (
        <div className="mb-4">
          <Refusal error={failure} message={failureMessage} />
        </div>
      ) : null}

      {/*
        Lapsing does not refund, deliberately.

        A refund is money leaving the agency and belongs on the payment it was received on, with its
        own receipt and its own reason. Doing it as a side effect of pressing "lapse" would put a
        reversal in the ledger nobody decided on — so the API says whether one is owed and this asks.
      */}
      {refund?.due && refund.amount > 0 ? (
        <div className="mb-6">
          <Alert tone="warn" title={`${refund.amountLabel} is owed back to ${buyer.name}.`}>
            The fee on this reservation was refundable. Nothing has been sent back: a refund is
            reversed through the receipt it arrived on, so that it carries its own reason and appears
            in the ledger as the decision it is.
            <ul className="mt-2 space-y-1">
              {data.receipts
                .filter((receipt) => receipt.status === "confirmed")
                .map((receipt) => (
                  <li key={receipt.id}>
                    <Link className="font-semibold underline" to={`/app/payments/${receipt.id}`}>
                      {receipt.receiptNumber}
                    </Link>{" "}
                    — reverse it there.
                  </li>
                ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      {reservation.state === "expired" ? (
        <div className="mb-6">
          <Alert tone="warn" title={`This ran out on ${reservation.expiresOn}.`}>
            It still stands until it is lapsed or extended — nothing has been released. Extend it to
            carry on, or lapse it to put {plot ? plot.label : `the ${noun.one}`} back on the market.
          </Alert>
        </div>
      ) : null}

      {purchase ? (
        <div className="mb-6">
          <Alert tone="success" title="This became a sale.">
            <Link className="font-semibold underline" to={`/app/development/sales/${purchase.id}`}>
              {purchase.reference}
            </Link>{" "}
            — the holding fee was applied to the plan as its first payment.
          </Alert>
        </div>
      ) : null}

      {/* ----------------------------------------------------------- the money */}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Fee quoted</p>
          {/*
            A fee nobody named is not a fee of nothing.

            `fee.set` is false when neither the property nor this reservation named one, and ₦0.00
            would tell the buyer there is nothing to pay — which is a decision somebody is entitled
            to make and therefore not one to arrive at by accident.
          */}
          <p className="mt-1 text-2xl font-semibold" data-fee-quoted>
            {fee.set ? fee.quotedLabel : <span className="text-ink-400">No fee set</span>}
          </p>
          {fee.set && reservation.feeBasis === "percent" && reservation.quotedPrice !== null ? (
            <p className="text-xs text-ink-500">
              {Number((reservation.feeValue ?? 0) / 100)}% of the price on the day it was taken
            </p>
          ) : null}
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Received</p>
          <p className="mt-1 text-2xl font-semibold">{fee.paidLabel}</p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Outstanding</p>
          <p className="mt-1 text-2xl font-semibold">{fee.set ? fee.outstandingLabel : <span className="text-ink-400">—</span>}</p>
        </Card>
        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Expires</p>
          <p className="mt-1 text-2xl font-semibold">
            {reservation.expiresOn ?? <span className="text-ink-400">Never</span>}
          </p>
          <p className="text-xs text-ink-500">
            {reservation.refundable ? "The fee is refundable." : "The fee is not refundable."}
          </p>
        </Card>
      </div>

      {/* --------------------------------------------------------------- doing */}

      {standing || reservation.state === "expired" ? (
        <Card className="mb-6">
          <CardTitle>Actions</CardTitle>
          <div className="flex flex-wrap gap-2">
            {can("payment.record") && fee.outstanding > 0 ? (
              <Button onClick={() => setDoing(doing === "fee" ? null : "fee")}>
                {doing === "fee" ? "Cancel" : "Record the fee"}
              </Button>
            ) : null}
            {can("lease.edit") ? (
              <Button variant="secondary" onClick={() => setDoing(doing === "convert" ? null : "convert")}>
                {doing === "convert" ? "Cancel" : "Convert to a sale"}
              </Button>
            ) : null}
            {can("lease.edit") ? (
              <Button variant="secondary" onClick={() => setDoing(doing === "extend" ? null : "extend")}>
                {doing === "extend" ? "Cancel" : reservation.expiresOn ? "Extend it" : "Give it an expiry"}
              </Button>
            ) : null}
            {can("lease.edit") ? (
              <Button variant="ghost" onClick={() => setDoing(doing === "lapse" ? null : "lapse")}>
                {doing === "lapse" ? "Cancel" : "Lapse it"}
              </Button>
            ) : null}
          </div>

          {doing === "fee" ? (
            <form
              className="mt-4 grid gap-4 border-t border-ink-100 pt-4 sm:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault();
                const values = formValues(event.currentTarget);
                recordFee.mutate({
                  amount: minor(String(values.amount ?? "")) ?? undefined,
                  method: values.method,
                  receivedOn: values.receivedOn,
                  reference: values.reference || null,
                });
              }}
            >
              <Field
                label="Amount received"
                htmlFor="amount"
                hint="Left empty, it is whatever is still outstanding on the quoted fee."
              >
                <MoneyInput id="amount" name="amount" placeholder={String(fee.outstanding / 100)} />
              </Field>
              <Field label="Method" htmlFor="method">
                <Select id="method" name="method" defaultValue="bank_transfer">
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="pos">POS</option>
                  <option value="cheque">Cheque</option>
                  <option value="card">Card</option>
                  <option value="mobile_money">Mobile money</option>
                </Select>
              </Field>
              <Field label="Date received" htmlFor="receivedOn" required>
                <Input id="receivedOn" name="receivedOn" type="date" required defaultValue={data.today} />
              </Field>
              <Field label="Payer's reference" htmlFor="reference">
                <Input id="reference" name="reference" placeholder="Transfer narration" />
              </Field>
              <div className="sm:col-span-4">
                <Button type="submit" disabled={recordFee.isPending}>
                  {recordFee.isPending ? "Recording…" : "Record the fee"}
                </Button>
                <p className="mt-2 text-xs text-ink-500">
                  An ordinary receipt in your own series. Until this becomes a sale it sits as credit on the buyer's
                  account, which is what money received against nothing owed actually is.
                </p>
              </div>
            </form>
          ) : null}

          {doing === "extend" ? (
            <form
              className="mt-4 flex flex-wrap items-end gap-3 border-t border-ink-100 pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                const values = formValues(event.currentTarget);
                /* A date, not a period. An extension is "until the end of the month", never
                   "fourteen more days from a day that has already gone". Empty takes the expiry
                   off altogether, which is a deliberate null rather than a missing key. */
                extend.mutate({ expiresOn: values.expiresOn || null });
              }}
            >
              <Field label="Stands until" htmlFor="expiresOn" hint="Leave it empty and it no longer expires at all.">
                <Input id="expiresOn" name="expiresOn" type="date" defaultValue={reservation.expiresOn ?? ""} />
              </Field>
              <Button type="submit" disabled={extend.isPending}>
                {extend.isPending ? "Saving…" : "Save the date"}
              </Button>
            </form>
          ) : null}

          {doing === "lapse" ? (
            <form
              className="mt-4 flex flex-wrap items-end gap-3 border-t border-ink-100 pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                const values = formValues(event.currentTarget);
                lapse.mutate({ reason: values.reason });
              }}
            >
              <div className="min-w-[18rem] flex-1">
                <Field
                  label="Why"
                  htmlFor="reason"
                  required
                  hint={
                    reservation.holds && plot
                      ? `${plot.label} goes back on the market.`
                      : "Nothing is released — this reservation is not holding anything."
                  }
                >
                  <Input id="reason" name="reason" required minLength={3} placeholder="The buyer withdrew." />
                </Field>
              </div>
              <Button type="submit" variant="danger" disabled={lapse.isPending}>
                {lapse.isPending ? "Ending…" : "Lapse it"}
              </Button>
            </form>
          ) : null}

          {doing === "convert" ? (
            <ConvertForm reservation={data} onCancel={() => setDoing(null)} />
          ) : null}
        </Card>
      ) : null}

      {reservation.state === "lapsed" && reservation.lapsedReason ? (
        <div className="mb-6">
          <Alert tone="muted" title="This lapsed.">
            {reservation.lapsedReason}
          </Alert>
        </div>
      ) : null}

      {/* ------------------------------------------------------------ the terms */}

      <div className="mb-6 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardTitle hint="Copied from the property when this was taken, and frozen here. Changing the property's defaults does not move them.">
            The terms it was taken on
          </CardTitle>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Fee</dt>
              <dd className="mt-0.5">
                {fee.set
                  ? reservation.feeBasis === "percent"
                    ? `${Number((reservation.feeValue ?? 0) / 100)}% — ${fee.quotedLabel}`
                    : fee.quotedLabel
                  : "None set"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Refundable</dt>
              <dd className="mt-0.5">{reservation.refundable ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Off the market</dt>
              <dd className="mt-0.5">
                {reservation.holds ? `Yes, while this stands` : `No — the ${noun.one} is still on offer`}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Expires</dt>
              <dd className="mt-0.5">{reservation.expiresOn ?? "Never"}</dd>
            </div>
            {reservation.notes ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-semibold uppercase tracking-wider text-ink-400">Notes</dt>
                <dd className="mt-0.5">{reservation.notes}</dd>
              </div>
            ) : null}
          </dl>
        </Card>

        <Card>
          <CardTitle>Buyer</CardTitle>
          <p className="font-medium">
            <Link className="text-brand-700 hover:underline" to={`/app/clients/${buyer.id}`}>
              {buyer.name}
            </Link>
          </p>
          <p className="text-sm text-ink-500">{[buyer.phone, buyer.email].filter(Boolean).join(" · ") || "No contact recorded."}</p>
          <p className="mt-3 text-sm text-ink-600">
            {plot ? (
              <>
                Holding <span className="font-medium">{plot.label}</span> in{" "}
                <Link className="text-brand-700 hover:underline" to={`/app/development/properties/${estate.id}`}>
                  {estate.name}
                </Link>
                .
              </>
            ) : (
              <>
                Reserved into{" "}
                <Link className="text-brand-700 hover:underline" to={`/app/development/properties/${estate.id}`}>
                  {estate.name}
                </Link>{" "}
                with no {noun.one} chosen yet.
              </>
            )}
          </p>
        </Card>
      </div>

      {/* --------------------------------------------------------- the receipts */}

      <Card padded={false}>
        <div className="border-b border-ink-200 p-4">
          <CardTitle hint="Ordinary receipts in your own series. When this becomes a sale they are applied to the plan rather than asked for again.">
            Receipts
          </CardTitle>
        </div>
        {!data.receipts.length ? (
          <p className="p-6 text-center text-sm text-ink-500">Nothing has been received against this reservation.</p>
        ) : (
          <Table
            head={
              <tr>
                <th>Receipt</th>
                <th>Date</th>
                <th>Method</th>
                <th className="num">Amount</th>
                <th>Status</th>
              </tr>
            }
          >
            {data.receipts.map((receipt) => (
              <tr key={receipt.id}>
                <td>
                  <Link className="text-brand-700 hover:underline" to={`/app/payments/${receipt.id}`}>
                    {receipt.receiptNumber}
                  </Link>
                </td>
                <td className="text-ink-600">{receipt.receivedOn}</td>
                <td className="text-ink-600">{receipt.method.replace("_", " ")}</td>
                {/* The app's own formatter, so this column reads the same as every other money
                    column in the product rather than in whatever notation `Intl` reaches for. */}
                <td className="num">{fmt.money(receipt.amount)}</td>
                <td>
                  <Badge tone={receipt.status === "reversed" ? "danger" : "success"}>{receipt.status}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------- converting */

interface Scheduled {
  sequence: number;
  label: string;
  dueDate: string;
  amountLabel: string;
}

/**
 * Turning a reservation into a sale.
 *
 * **The buyer and the property are not on this form**, because they are not decisions any more:
 * they were made when the reservation was taken, and the API refuses to move them for exactly the
 * reason a screen should not offer to — a conversion that quietly moved a holding fee onto a
 * different person's account is a receipt somebody else is holding.
 *
 * Everything else is the sale form's own body, and the schedule is previewed through the same
 * `POST /purchases/preview` the sale form uses, so the plan a buyer is shown here and the plan they
 * would be shown there cannot differ.
 */
function ConvertForm({ reservation, onCancel }: { reservation: ReservationDetail; onCancel: () => void }) {
  const navigate = useNavigate();
  const fmt = useFormat();
  const [price, setPrice] = useState(reservation.fee.quotedPrice ? String(reservation.fee.quotedPrice / 100) : "");
  const [deposit, setDeposit] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [instalments, setInstalments] = useState("24");
  const [startDate, setStartDate] = useState(reservation.today);
  const [plotId, setPlotId] = useState(reservation.plot?.id ?? "");
  const [marketerId, setMarketerId] = useState("");
  const [commissionBp, setCommissionBp] = useState("");
  const [commissionBasis, setCommissionBasis] = useState("");
  const [preview, setPreview] = useState<{ schedule: Scheduled[]; totalLabel: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data: property } = useQuery({
    queryKey: ["estate", reservation.estate.id],
    queryFn: () =>
      api.get<{ plots: Array<{ id: string; label: string; status: string; sellable: boolean }> }>(
        `/estates/${reservation.estate.id}`,
      ),
  });

  useEffect(() => {
    const total = minor(price) ?? 0;
    if (!total || !startDate) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await api.post<{ schedule: Scheduled[]; totalLabel: string }>("/purchases/preview", {
          price: total,
          deposit: minor(deposit) ?? 0,
          frequency,
          instalments: Number(instalments) || 1,
          startDate,
        });
        if (!cancelled) {
          setPreview(result);
          setPreviewError(null);
        }
      } catch (failure) {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(failure instanceof Error ? failure.message : null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [price, deposit, frequency, instalments, startDate]);

  const convert = useApiMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ id: string }>(`/reservations/${reservation.reservation.id}/convert`, input),
    invalidates: [["reservations"], ["purchases"], ["sales"], ["developer-summary"], ["estate", reservation.estate.id]],
    onSuccess: (result) => navigate(`/app/development/sales/${result.id}`),
  });

  return (
    <div className="mt-4 border-t border-ink-100 pt-4">
      {convert.errorMessage ? (
        <div className="mb-4">
          <Refusal error={convert.error} message={convert.errorMessage} />
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            const values = formValues(event.currentTarget);
            convert.mutate({
              plotId: plotId || null,
              price: minor(price) ?? 0,
              deposit: minor(deposit) ?? 0,
              frequency,
              instalments: Number(instalments) || 1,
              startDate,
              lateFeeBps: Math.round(Number(values.lateFee || 0) * 100),
              earlyDiscountBps: Math.round(Number(values.earlyDiscount || 0) * 100),
              notes: values.notes || null,
              /* All three left out is the ordinary case: the rate comes off the tree and the basis
                 off the property, and both are frozen on the sale from that moment. */
              marketerId: marketerId || undefined,
              commissionBp: commissionBp.trim() === "" ? undefined : Math.round(Number(commissionBp) * 100),
              commissionBasis: commissionBasis || undefined,
            });
          }}
        >
          <Field
            label={titleCase(reservation.noun.one)}
            htmlFor="convert-plot"
            hint={`Optional. Reserving one thing and selling another puts the first back on the market.`}
          >
            <Select id="convert-plot" value={plotId} onChange={(event) => setPlotId(event.target.value)}>
              <option value="">Allocate later</option>
              {(property?.plots ?? [])
                .filter((row) => row.sellable && (row.status === "available" || row.id === reservation.plot?.id))
                .map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.label}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Agreed price" htmlFor="convert-price" required>
            <MoneyInput id="convert-price" value={price} onChange={setPrice} required placeholder="25,000,000" />
          </Field>
          <Field label="Deposit" htmlFor="convert-deposit" hint="Payable on signing, ahead of the schedule.">
            <MoneyInput id="convert-deposit" value={deposit} onChange={setDeposit} placeholder="5,000,000" />
          </Field>
          <Field label="Instalment frequency" htmlFor="convert-frequency">
            <Select id="convert-frequency" value={frequency} onChange={(event) => setFrequency(event.target.value)}>
              {PURCHASE_FREQUENCIES.filter((code) => code !== "custom").map((code) => (
                <option key={code} value={code}>
                  {code.charAt(0).toUpperCase() + code.slice(1)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Number of instalments" htmlFor="convert-instalments" required>
            <Input
              id="convert-instalments"
              type="number"
              min={1}
              max={240}
              value={instalments}
              onChange={(event) => setInstalments(event.target.value)}
              required
            />
          </Field>
          <Field label="First payment due" htmlFor="convert-start" required>
            <Input
              id="convert-start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              required
            />
          </Field>
          <Field label="Late payment fee" htmlFor="lateFee" hint="A percentage of what is outstanding on an overdue instalment.">
            <Input id="lateFee" name="lateFee" type="number" min={0} max={100} step="0.01" placeholder="2.5" />
          </Field>
          <Field label="Early settlement discount" htmlFor="earlyDiscount" hint="A percentage off instalments not yet due.">
            <Input id="earlyDiscount" name="earlyDiscount" type="number" min={0} max={100} step="0.01" placeholder="5" />
          </Field>

          <Field label="Sold by" htmlFor="marketerId" hint="Leave it as nobody where the office sold it.">
            <MarketerPicker value={marketerId} onChange={setMarketerId} />
          </Field>
          {marketerId ? (
            <CommissionOverrides
              bp={commissionBp}
              basis={commissionBasis}
              onBp={setCommissionBp}
              onBasis={setCommissionBasis}
            />
          ) : null}

          <div className="sm:col-span-2">
            <Field label="Notes" htmlFor="notes">
              <Input id="notes" name="notes" placeholder="Terms agreed at the site office." />
            </Field>
          </div>

          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <Button type="submit" disabled={convert.isPending || !price}>
              {convert.isPending ? "Converting…" : "Record the sale"}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>

        <Card>
          <CardTitle hint="The schedule these terms produce. Review it with the buyer before recording the sale.">
            Payment schedule
          </CardTitle>
          {reservation.fee.paid > 0 ? (
            <p className="mb-3 rounded-[var(--radius-control)] bg-brand-50 p-2 text-xs text-brand-800">
              {fmt.money(reservation.fee.paid)} has already been received as a holding fee. It is applied to the first
              payment on this plan rather than asked for again.
            </p>
          ) : null}
          {previewError ? (
            <p className="text-sm text-danger-600">{previewError}</p>
          ) : !preview ? (
            <p className="text-sm text-ink-500">Enter a price and the schedule appears here.</p>
          ) : (
            <>
              <p className="mb-3 text-sm text-ink-600">
                {preview.schedule.length} payments, {preview.totalLabel} in total.
              </p>
              <ul className="max-h-[360px] space-y-1 overflow-y-auto text-sm">
                {preview.schedule.map((row) => (
                  <li key={row.sequence} className="flex justify-between gap-3 border-b border-ink-100 py-1.5 last:border-0">
                    <span className="text-ink-600">
                      {row.label}
                      <span className="block text-xs text-ink-400">{row.dueDate}</span>
                    </span>
                    <span className="num font-medium">{row.amountLabel}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
